import type { Terminal } from '@xterm/xterm';

const HIGHLIGHT_BG = '#FFB800';
const HIGHLIGHT_FG = '#08090B';
const MAX_MARKS = 200;
const REDRAW_DEBOUNCE = 120;

type Disposable = { dispose: () => void };

export type LiveConsoleHighlighter = {
    setNeedle: (_needle: string) => void;
    refresh: () => void;
    dispose: () => void;
};

export const createHighlighter = (term: Terminal): LiveConsoleHighlighter => {
    let needle = '';
    let marks: Disposable[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;

    const clear = () => {
        for (const mark of marks) mark.dispose();
        marks = [];
    };

    const draw = () => {
        clear();
        if (!needle) return;

        const buffer = term.buffer.active;
        const lowered = needle.toLowerCase();
        const cursorLine = buffer.baseY + buffer.cursorY;
        const firstVisible = buffer.viewportY;
        const lastVisible = Math.min(buffer.length, firstVisible + term.rows);
        let drawn = 0;

        for (let y = firstVisible; y < lastVisible && drawn < MAX_MARKS; y++) {
            const line = buffer.getLine(y);
            if (!line) continue;
            const text = line.translateToString(true).toLowerCase();
            let at = text.indexOf(lowered);
            while (at !== -1 && drawn < MAX_MARKS) {
                const marker = term.registerMarker(y - cursorLine);
                if (marker) {
                    marks.push(marker);
                    const decoration = term.registerDecoration({
                        marker,
                        x: at,
                        width: needle.length,
                        backgroundColor: HIGHLIGHT_BG,
                        foregroundColor: HIGHLIGHT_FG,
                        layer: 'bottom',
                    });
                    if (decoration) marks.push(decoration);
                }
                drawn++;
                at = text.indexOf(lowered, at + lowered.length);
            }
        }
    };

    const schedule = () => {
        if (timer) return;
        timer = setTimeout(() => {
            timer = undefined;
            draw();
        }, REDRAW_DEBOUNCE);
    };

    return {
        setNeedle: (next: string) => {
            needle = next.trim();
            draw();
        },
        refresh: () => {
            if (needle) schedule();
        },
        dispose: () => {
            if (timer) clearTimeout(timer);
            clear();
        },
    };
};
